import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useCan } from "@/auth/permissions";
import { assistantApi, ASSISTANT_QK, type AssistantCalendar, type AssistantTodos, type StripItem, type TodayStrip } from "@/services/assistant";
import { addDays, pacificToday } from "@/pages/dispatch/boardDisplay";
import { buildVersion } from "./buildVersion";
import { SUB_QK, subscriptionApi } from "@/services/subscription";
// ═ COMPANY-STRIP BEGIN ═
import { HomeSocialCard } from "@/pages/marketing/HomeSocialCard";   // MKT-1 · 「内容营销 · 本周」卡（没开通不渲染）
import { useMarketingEnabled } from "@/pages/marketing/api";
// ═ COMPANY-STRIP END ═

/**
 * 首页「经营助手」（订阅版 · 仅个人站）—— 契约：designs/mockups/theme/个人站订阅版-样稿.html ① 首页（tag saas-mockup-v1）。
 * 工作日历（有出团 / 有待办打点）+ 分类待办（每格带数字、点了直达筛好的列表）+ 今日出团条 + 订单统计 + 版本号。
 * 🔴 数字全部读后端（待办数字与点进去的列表同一个查询条件）；后端没有数据来源的格子不返回，这里就不画；颜色只用主题令牌（brand / accent / slate）。
 * 内容营销卡：MKT-1 起按契约接上（内容营销已启用才出，与订单统计并排，样稿 ① 右下）；右下角「更新说明 / 帮助教程」链接 = SAAS-2（帮助教程没配地址就不出）。
 * 数据：GET /api/v1/assistant/{todos,calendar,today-strip,order-stats}。
 */
const WEEK_MON = ["一", "二", "三", "四", "五", "六", "日"];      // 样稿日历从周一起
const WEEK_CN = ["日", "一", "二", "三", "四", "五", "六"];
// 分组标记色（样稿 .grp：订单类 = 强调色 / 合作类 = 信息色 / 运营类 = 危险色）
const GROUP_ACCENT: Record<string, string> = { orders: "bg-accent", partners: "bg-sky-700", ops: "bg-red-600" };
const TIER_LABEL = { ample: "充足", tight: "紧张", full: "已满" } as const;
const AXIS = ["06:00", "08:00", "10:00", "12:00", "14:00", "16:00"];

function ymOf(d: string) {
  return d.slice(0, 7);
}
function shiftMonth(ym: string, n: number) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function money(cents: number | null | undefined) {
  return cents === null || cents === undefined ? "—" : "$" + Math.round(cents / 100).toLocaleString("en-US");
}
function startCol(t: string | null): number {
  if (!t) return 0;
  const h = Number(t.slice(0, 2));
  return Math.min(5, Math.max(0, Math.floor((h - 6) / 2)));
}

const card = "rounded-md border border-slate-200 bg-white";

export function AssistantHome() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canBoard = useCan("dispatch.center.view");
  const canOrders = useCan("orders.orders.list");
  const today = pacificToday();
  const [month, setMonth] = useState(ymOf(today));

  const todos = useQuery({ queryKey: [...ASSISTANT_QK, "todos"], queryFn: assistantApi.todos, refetchInterval: 120_000 });
  const cal = useQuery({ queryKey: [...ASSISTANT_QK, "calendar", month], queryFn: () => assistantApi.calendar(month), enabled: canBoard });
  const toolbar = useQuery({ queryKey: SUB_QK.toolbar, queryFn: subscriptionApi.toolbar, staleTime: 5 * 60_000 });   // SAAS-2 · 帮助教程地址
  const help = toolbar.data?.help_center_url ?? null;
  const strip = useQuery({ queryKey: [...ASSISTANT_QK, "strip", today], queryFn: () => assistantApi.todayStrip(today), enabled: canBoard, refetchInterval: 120_000 });

  const td = new Date(today + "T00:00:00");
  const dateText = `${td.getMonth() + 1} 月 ${td.getDate()} 日 周${WEEK_CN[td.getDay()]}`;
  const refresh = () => qc.invalidateQueries({ queryKey: ASSISTANT_QK });
  let sideCard: ReactNode = null;
  // ═ COMPANY-STRIP BEGIN ═
  const marketingOn = useMarketingEnabled();
  if (marketingOn) sideCard = <HomeSocialCard />;
  // ═ COMPANY-STRIP END ═

  return (
    <div className="flex flex-col gap-3.5 p-6 lg:p-8" data-testid="assistant-home">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-bold text-slate-900">经营助手</h1>
        <span className="text-[13px] text-slate-400">{dateText}</span>
        <span className="flex-1" />
        <button type="button" onClick={refresh} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[13px] text-slate-900 hover:border-brand hover:text-brand">
          刷新
        </button>
        <Link to="/orders/new" className="rounded-md bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:bg-brand-hover">
          ＋ 新建订单
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[330px_minmax(0,1fr)]">
        {canBoard && <CalendarCard month={month} onMonth={setMonth} today={today} q={cal} onPick={(d) => navigate(`/dispatch/board?from=${d}`)} />}
        <TodoCard q={todos} wide={!canBoard} />
      </div>

      {canBoard && <StripCard q={strip} onOpen={(link) => navigate(link)} />}
      {sideCard ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_360px]">
          {canOrders && <StatsCard today={today} />}
          {sideCard}
        </div>
      ) : (
        canOrders && <StatsCard today={today} />
      )}

      {/* SAAS-2 · C：版本号 · 更新说明（进列表页）· 帮助教程（没配地址就不出） */}
      <div className="flex items-center justify-end gap-2.5 text-[11.5px] text-slate-400" data-testid="app-version">
        <span>版本 {buildVersion()}</span>
        <span>·</span>
        <Link to="/release-notes" className="text-brand hover:underline" data-testid="version-notes">更新说明</Link>
        {help && (
          <>
            <span>·</span>
            <a href={help} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline" data-testid="version-help">帮助教程</a>
          </>
        )}
      </div>
    </div>
  );
}

// ── 工作日历 ──────────────────────────────────────────────────────────────
function CalendarCard({ month, onMonth, today, q, onPick }: { month: string; onMonth: (m: string) => void; today: string; q: UseQueryResult<AssistantCalendar>; onPick: (d: string) => void }) {
  const [y, m] = month.split("-").map(Number);
  const byDate = useMemo(() => new Map((q.data?.days ?? []).map((d) => [d.date, d])), [q.data]);
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7;          // 周一 = 0
  const n = new Date(y, m, 0).getDate();
  const iso = (dt: Date) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const weeks = Math.ceil((lead + n) / 7);
  // 整周铺满：前后月份的日子灰显（不打点，点了也跳调度中心那一天）
  const cells = Array.from({ length: weeks * 7 }, (_, i) => iso(new Date(y, m - 1, 1 - lead + i)));
  return (
    <div className={`${card} p-3`} data-testid="assistant-calendar">
      <div className="mb-2 flex items-center justify-between">
        <b className="text-[13px] text-slate-900">{m} 月工作日历</b>
        <span className="flex gap-1 text-slate-400">
          <button type="button" aria-label="上个月" className="px-1.5 hover:text-brand" onClick={() => onMonth(shiftMonth(month, -1))}>‹</button>
          <button type="button" aria-label="下个月" className="px-1.5 hover:text-brand" onClick={() => onMonth(shiftMonth(month, 1))}>›</button>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[12px]">
        {WEEK_MON.map((w) => (
          <div key={w} className="text-slate-400">{w}</div>
        ))}
        {cells.map((d) => {
          const info = byDate.get(d);
          const isToday = d === today;
          const dim = ymOf(d) !== month;
          return (
            <button
              type="button"
              key={d}
              data-date={d}
              onClick={() => onPick(d)}
              className={`relative rounded-md pb-2 pt-1.5 ${isToday ? "bg-brand font-bold text-white" : dim ? "text-slate-300 hover:bg-slate-50" : "text-slate-900 hover:bg-slate-50"}`}
            >
              {Number(d.slice(8))}
              {info && (info.tours > 0 || info.alerts > 0) && (
                <span className="absolute bottom-0.5 left-1/2 flex -translate-x-1/2 gap-0.5">
                  {info.tours > 0 && <i data-dot="tour" className={`h-[5px] w-[5px] rounded-full ${isToday ? "bg-white" : "bg-blue-400"}`} />}
                  {info.alerts > 0 && <i data-dot="alert" className={`h-[5px] w-[5px] rounded-full ${isToday ? "bg-white" : "bg-accent"}`} />}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {q.isError && <div className="mt-2 text-[12px] text-red-600">日历加载失败</div>}
      <div className="mt-2 flex gap-3 text-[11.5px] text-slate-400">
        <span><i className="mr-1 inline-block h-[7px] w-[7px] rounded-full bg-blue-400 align-[1px]" />有出团</span>
        <span><i className="mr-1 inline-block h-[7px] w-[7px] rounded-full bg-accent align-[1px]" />有待办 / 异常</span>
      </div>
    </div>
  );
}

// ── 分类待办 ──────────────────────────────────────────────────────────────
function TodoCard({ q, wide }: { q: UseQueryResult<AssistantTodos>; wide?: boolean }) {
  return (
    <div className={`${card} flex flex-col gap-2.5 px-3.5 py-3 ${wide ? "lg:col-span-2" : ""}`} data-testid="assistant-todos">
      {q.isLoading && <div className="text-[12px] text-slate-400">加载中…</div>}
      {q.isError && <div className="text-[12px] text-red-600">待办加载失败</div>}
      {q.data?.groups.map((g) => (
        <div key={g.key} className="flex flex-col gap-2" data-group={g.key}>
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-slate-900">
            <span className={`h-3.5 w-1 rounded-sm ${GROUP_ACCENT[g.key] ?? "bg-brand"}`} />
            {g.label}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {g.items.map((it) => (
              <Link
                key={it.key}
                to={it.link}
                title={it.source}
                data-key={it.key}
                className="flex items-center justify-between rounded-md bg-slate-50 px-2.5 py-2 text-[12.5px] text-slate-600 hover:bg-blue-50"
              >
                <span>{it.label}</span>
                <b className={`text-[15px] ${it.count > 0 ? "text-accent" : "text-slate-400"}`}>{it.count}</b>
              </Link>
            ))}
          </div>
        </div>
      ))}
      {q.data && q.data.groups.length === 0 && <div className="text-[12px] text-slate-400">当前账号没有可看的待办分类</div>}
    </div>
  );
}

// ── 今日出团条 ────────────────────────────────────────────────────────────
function barText(it: StripItem) {
  const code = it.product_codes[0] ?? it.label;
  // 条内文字以出发时间开头（条的长度只是位置标记，不代表行程时长）
  const parts = [it.day_no === 1 ? it.start_time : null, code, it.day_no > 1 ? `第 ${it.day_no} 天` : null, it.driver?.label ?? null, it.seat_tier ? TIER_LABEL[it.seat_tier] : null].filter(Boolean);
  return parts.join(" · ");
}
function StripCard({ q, onOpen }: { q: UseQueryResult<TodayStrip>; onOpen: (link: string) => void }) {
  const data = q.data;
  const rows = (data?.lines ?? []).flatMap((ln) => packLine(ln.items).map((cells, i) => ({ ln, cells, first: i === 0 })));
  return (
    <div className={`${card} p-3`} data-testid="assistant-strip">
      <div className="mb-2 font-semibold text-slate-900">
        今日出团条 <span className="font-normal text-slate-400">· 点格子进调度</span>
        <span className="ml-2 text-[11.5px] font-normal text-slate-400">条长不代表时长</span>
      </div>
      {q.isError && <div className="text-[12px] text-red-600">今日出团条加载失败</div>}
      {data && rows.length === 0 && data.unattached.length === 0 && <div className="text-[12.5px] text-slate-400">今天没有出团的车次</div>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <div className="grid min-w-[760px] items-center gap-x-1.5 gap-y-1.5 text-[12px]" style={{ gridTemplateColumns: "76px repeat(6, minmax(110px, 1fr))" }}>
            <span className="text-slate-400">线路</span>
            {AXIS.map((a) => (
              <span key={a} className="text-slate-400">{a}</span>
            ))}
            {rows.map(({ ln, cells, first }, i) => (
              <StripRow key={`${ln.key}-${cells[0].item.run_id}`} row={i + 2} line={first ? ln.name : ""} cells={cells} onOpen={onOpen} />
            ))}
          </div>
        </div>
      )}
      {data && data.unattached.length > 0 && (
        <div className="mt-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-[12px] text-slate-600">
          有 {data.unattached.length} 张订单当天出发但还没挂车次：{data.unattached.map((u) => u.order_no).join("、")}
        </div>
      )}
    </div>
  );
}
interface BarCell {
  item: StripItem;
  col: number;
  span: number;
}
/** 条的位置：出发时间落到哪一个 2 小时格；第 2 天起 / 没填出发时间的铺满整行（不拿猜的时间画位置）；宽度只是标记，不代表行程时长（没有结束时间的数据）。 */
function placeItem(it: StripItem): BarCell {
  const full = it.day_no > 1 || !it.start_time;
  const col = full ? 0 : startCol(it.start_time);
  return { item: it, col, span: full ? 6 : Math.min(2, 6 - col) };
}
/** 同一线路的车次：不重叠就并排放在同一行，重叠才另起一行（样稿里 SF 线两条在同一行）。 */
function packLine(items: StripItem[]): BarCell[][] {
  const rows: BarCell[][] = [];
  for (const it of items.map(placeItem)) {
    const row = rows.find((r) => r.every((c) => c.col + c.span <= it.col || it.col + it.span <= c.col));
    if (row) row.push(it);
    else rows.push([it]);
  }
  return rows;
}
function StripRow({ row, line, cells, onOpen }: { row: number; line: string; cells: BarCell[]; onOpen: (link: string) => void }) {
  // 每一格都写明行号（第 1 行是时间轴），否则栅格自动排位会把「线路」名挤到别的行
  return (
    <>
      <span className="text-slate-900" style={{ gridRow: row, gridColumn: 1 }}>{line}</span>
      {cells.map((c) => (
        <RowBar key={c.item.run_id} row={row} col={c.col} span={c.span} item={c.item} onOpen={onOpen} />
      ))}
    </>
  );
}
function RowBar({ row, col, span, item, onOpen }: { row: number; col: number; span: number; item: StripItem; onOpen: (link: string) => void }) {
  const warn = item.alerts.length > 0;
  return (
    <>
      <button
        type="button"
        data-run={item.run_id}
        data-warn={warn ? "1" : undefined}
        title={[item.start_time ? `出发 ${item.start_time}` : "出发时间未填", ...item.alerts.map((a) => a.label)].join(" · ")}
        onClick={() => onOpen(item.link)}
        className={`truncate rounded px-1.5 py-1 text-left text-white ${warn ? "bg-accent" : "bg-brand"}`}
        style={{ gridRow: row, gridColumn: `${col + 2} / span ${span}` }}
      >
        {barText(item)}
        {warn && ` · ${item.alerts[0].label} ⚠`}
        {!item.start_time && item.day_no === 1 && " · 时间未填"}
      </button>
    </>
  );
}

// ── 订单统计 ──────────────────────────────────────────────────────────────
type Range = "14d" | "month" | "custom";
function StatsCard({ today }: { today: string }) {
  const [range, setRange] = useState<Range>("14d");
  const [cFrom, setCFrom] = useState(addDays(today, -13));
  const [cTo, setCTo] = useState(today);
  const [from, to] = range === "14d" ? [addDays(today, -13), today] : range === "month" ? [`${ymOf(today)}-01`, today] : [cFrom, cTo];
  const q = useQuery({ queryKey: [...ASSISTANT_QK, "stats", from, to], queryFn: () => assistantApi.orderStats(from, to), enabled: !!from && !!to && from <= to });
  const d = q.data;
  const showAmount = !!d && d.total_amount_cents !== null;
  const vals = (d?.days ?? []).map((x) => (showAmount ? x.amount_cents ?? 0 : x.count));
  const max = Math.max(1, ...vals);
  return (
    <div className={`${card} p-3`} data-testid="assistant-stats">
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-slate-900">订单统计</b>
        <span className="flex-1" />
        {([["14d", "近 14 天"], ["month", "本月"], ["custom", "自定义"]] as [Range, string][]).map(([k, l]) => (
          <button key={k} type="button" aria-pressed={range === k} onClick={() => setRange(k)} className={`rounded-full border px-2.5 py-0.5 text-[12px] ${range === k ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-600"}`}>
            {l}
          </button>
        ))}
      </div>
      {range === "custom" && (
        <div className="mt-2 flex items-center gap-2 text-[12px] text-slate-600">
          <input type="date" aria-label="开始日期" value={cFrom} max={cTo} onChange={(e) => setCFrom(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" />
          <span>–</span>
          <input type="date" aria-label="结束日期" value={cTo} min={cFrom} onChange={(e) => setCTo(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" />
        </div>
      )}
      {q.isError && <div className="mt-2 text-[12px] text-red-600">订单统计加载失败</div>}
      {d && (
        <>
          <div className="my-1.5 flex flex-wrap gap-4 text-[13px] text-slate-400">
            {showAmount && <span>金额 <b data-testid="stats-amount" className="text-slate-900">{money(d.total_amount_cents)}</b></span>}
            <span>笔数 <b data-testid="stats-count" className="text-slate-900">{d.total_count}</b></span>
            {d.total_s4_count > 0 && <span>其中社媒带来 <b data-testid="stats-s4" className="text-accent">{d.total_s4_count} 笔</b></span>}
          </div>
          <div className="flex h-[120px] items-end gap-1.5 border-b border-slate-200 pt-2">
            {d.days.map((x, i) => (
              <div key={x.date} title={`${x.date} · ${x.count} 笔${showAmount ? " · " + money(x.amount_cents) : ""}`} className={`relative min-w-0 flex-1 rounded-t-sm ${x.date === today ? "bg-brand" : "bg-blue-400"}`} style={{ height: `${Math.max(2, (vals[i] / max) * 100)}%` }}>
                <span className="absolute -top-4 left-0 right-0 text-center text-[10px] text-slate-400">{x.count}</span>
              </div>
            ))}
          </div>
          <div className="mt-1 flex gap-1.5 text-[10.5px] text-slate-400">
            {d.days.map((x) => (
              <span key={x.date} className="min-w-0 flex-1 text-center">{Number(x.date.slice(5, 7))}/{Number(x.date.slice(8))}</span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
